import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = 'December 30, 2025';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <Icons.ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-primary font-semibold hover:text-blue-800"
          >
            Bible Aware
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 mb-4">
              Bible Aware ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Bible study platform.
            </p>
            <p className="text-gray-700">
              By using Bible Aware, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Account Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Name and email address</li>
              <li>Profile photo (if you sign in with Google)</li>
              <li>Organization membership and role</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Learning Activity</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Study and course progress</li>
              <li>Completion records</li>
              <li>Notes and highlights you create</li>
              <li>Questions you ask within studies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Content You Create</h3>
            <p className="text-gray-700 mb-4">If you are a content creator, we store the studies, courses, and media you create.</p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Form Responses</h3>
            <p className="text-gray-700">
              When you submit information through forms in studies (such as email signups, event RSVPs, or contact forms), that information is collected and shared with the organization that created the study.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide and maintain the service</li>
              <li>Save your learning progress</li>
              <li>Send notifications you've opted into (such as completion emails)</li>
              <li>Allow organizations to manage their members and content</li>
              <li>Improve the service</li>
            </ul>
          </section>

          {/* AI-Generated Content */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">AI-Generated Content</h2>
            <p className="text-gray-700 mb-4">
              Bible Aware uses Google's AI services to generate images, audio, and video for Bible studies. When you use these features, your prompts are sent to Google for processing. We do not share personal information with these AI services.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700">
              We use industry-standard security measures to protect your data, including encryption and access controls. While we strive to protect your information, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing</h2>
            <p className="text-gray-700 mb-4">We do not sell your personal information. We may share your data in these circumstances:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Within your organization:</strong> Administrators can see member names, emails, and learning progress</li>
              <li><strong>Form responses:</strong> Information you submit through study forms is shared with the organization that created the study</li>
              <li><strong>Legal requirements:</strong> If required by law or to protect our rights</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Access and update your profile information</li>
              <li>Request deletion of your account</li>
              <li>Leave an organization at any time</li>
              <li>Opt out of email notifications</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at the email address below.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Bible Aware is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of changes by updating the "Last updated" date on this page.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-gray-100 rounded-lg p-6">
              <p className="text-gray-700">
                <strong>Bible Aware</strong><br />
                Email: contact@churchaware.com
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} Bible Aware. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
